import { Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { AbstractListViewService, ListViewDataBase } from '@examdojo/core/list-view';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { IRowNode, ValueGetterFunc, ValueGetterParams } from 'ag-grid-community';
import { tap } from 'rxjs';
import { AbstractSimpleAgGridService } from '../list-view-ag-grid.service';
import { createFilterColumn, ExternalFilterColumn, ExternalFilterValue } from './list-view-external-filtering.model';

export type FormGroupValue = Record<string, ExternalFilterValue>;
@Injectable()
export class ListViewExternalFilteringService {
  constructor(
    private readonly listViewAgGridService: AbstractSimpleAgGridService,
    private readonly listViewService: AbstractListViewService,
  ) {
    this.setFilterColumnsFromData().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly formGroup = new FormGroup({});

  readonly filterColumns = signal<ExternalFilterColumn[]>([]);

  applyFilter() {
    const filterFunctions = this.filterColumns()
      .map((filterColumn) => {
        const formValue = filterColumn.formCtrl.getRawValue();

        if (!formValue) {
          return null;
        }

        return filterColumn.doesExternalFilterPassFunc(filterColumn.formCtrl.getRawValue());
      })
      .filter(isNotNullish);

    this.listViewService.gridApi?.setGridOption('doesExternalFilterPass', (node: IRowNode) => {
      if (node.data) {
        return filterFunctions.every((filterFunction) => filterFunction(node));
      }

      return true;
    });

    this.listViewService.gridApi?.setGridOption('isExternalFilterPresent', () => filterFunctions.length > 0);

    this.listViewService.gridApi?.onFilterChanged();
  }

  resetFilter(value?: FormGroupValue) {
    this.formGroup.reset(value ?? {});
  }

  setFilterColumnsFromData() {
    return this.listViewService.data$.pipe(
      tap((data) => {
        const filterColumns = this.listViewAgGridService.columnDefs
          .map((columnDef) => {
            const externalFilter = columnDef.externalFilter;

            if (!externalFilter) {
              return null;
            }

            const colId = columnDef.colId;
            const filterValueGetter = columnDef.filterValueGetter;

            if (!colId || !filterValueGetter) {
              // colId and filterValueGetter should be defined if externalFiltering is defined. TODO: dev log error
              return null;
            }

            const filterColumn = createFilterColumn(
              externalFilter,
              colId,
              filterValueGetter as ValueGetterFunc<ListViewDataBase>,
            );

            this.formGroup.setControl(colId, filterColumn.formCtrl);

            return filterColumn;
          })
          .filter(isNotNullish);

        if (!filterColumns.length) {
          return [];
        }

        const possibleDataOptionsForEveryFilterColumn = data.reduce(
          (acc, row) => {
            filterColumns.forEach((filterColumn) => {
              const value = filterColumn.filterValueGetter({ data: row } as ValueGetterParams<ListViewDataBase>);
              const set = acc[filterColumn.colId] ?? new Set<string | number>();

              if (value) {
                set.add(value);
                acc[filterColumn.colId] = set;
              }
            });

            return acc;
          },
          {} as Record<string, Set<string>>,
        );

        filterColumns.forEach((filterColumn) => {
          const values = Array.from(possibleDataOptionsForEveryFilterColumn[filterColumn.colId] ?? []);

          filterColumn.options = values;
        });

        return this.filterColumns.set(filterColumns);
      }),
    );
  }
}
